import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Separator } from "../components/ui/separator";
import Navigation from "../components/layout/Navigation";
import Footer from "../components/layout/Footer";
import QuoteBuilder from "../components/ui/QuoteBuilder";
import {
  Palette,
  Frame,
  Ruler,
  Building,
  Users,
  ArrowRight,
  CheckCircle,
  MessageSquare,
  Image,
  Layers,
} from "lucide-react";

const SERVICES = [
  {
    icon: Palette,
    title: "Custom Reproductions",
    description:
      "Museum-quality reproductions on your choice of substrate — archival paper, stretched canvas, aluminium dibond, acrylic face-mount, or natural timber.",
    details: [
      "Sizes from A4 to full-wall murals",
      "Colour-matched to original artwork",
      "Archival inks rated 100+ years",
    ],
  },
  {
    icon: Frame,
    title: "Professional Framing",
    description:
      "Hand-finished frames by Australian craftspeople. Choose from gallery black, white, natural timber, or bespoke options to complement any interior.",
    details: [
      "Conservation-grade materials",
      "UV-protective glazing available",
      "Ready-to-hang delivery",
    ],
  },
  {
    icon: Ruler,
    title: "Bespoke Sizing",
    description:
      "Need a specific dimension for an architectural feature wall or above a particular piece of furniture? We produce to your exact measurements.",
    details: [
      "Millimetre-precise output",
      "Multi-panel & triptych layouts",
      "Site-visit consultation available",
    ],
  },
  {
    icon: Building,
    title: "Commercial & Hospitality",
    description:
      "Transform lobbies, restaurants, hotels, and corporate offices with curated Aboriginal art installations that honour culture and elevate spaces.",
    details: [
      "Volume pricing for multi-room fit-outs",
      "Project management & installation",
      "Cultural narrative plaques included",
    ],
  },
  {
    icon: Users,
    title: "Wholesale & Trade",
    description:
      "Interior designers, architects, and resellers can access exclusive trade pricing, dedicated account management, and priority production scheduling.",
    details: [
      "Trade accounts with net-30 terms",
      "White-label packaging available",
      "Dedicated account manager",
    ],
  },
  {
    icon: Layers,
    title: "Licensing & Collaboration",
    description:
      "Licence Colin Isaacs' artwork for products, publications, events, or digital media. We work closely with you to ensure respectful cultural representation.",
    details: [
      "Usage rights tailored to your project",
      "High-res files delivered promptly",
      "Cultural consultation included",
    ],
  },
];

const PROCESS_STEPS = [
  {
    num: "01",
    title: "Submit Your Brief",
    text: "Use our Quote Builder or get in touch directly. Tell us about the artwork, size, substrate, and any framing preferences.",
  },
  {
    num: "02",
    title: "Receive Your Quote",
    text: "We'll respond within 24-48 hours with a detailed quote, production timeline, and any recommendations.",
  },
  {
    num: "03",
    title: "Approve & Produce",
    text: "Once approved, your piece enters our production queue. We'll keep you updated at each stage with progress photos.",
  },
  {
    num: "04",
    title: "Delivery & Installation",
    text: "Carefully packaged and shipped with tracking. For large installations, we coordinate professional hanging and placement.",
  },
];

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.1, duration: 0.5, ease: "easeOut" },
  }),
};

export default function CommissionsPage() {
  const [quoteOpen, setQuoteOpen] = useState(false);

  return (
    <div className="min-h-screen bg-background" data-testid="commissions-page">
      <Navigation />

      {/* Hero */}
      <section className="relative pt-36 pb-24 px-6 md:px-12 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-primary/5 to-transparent pointer-events-none" />
        <div className="max-w-4xl mx-auto text-center relative">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-sm uppercase tracking-[0.25em] text-primary mb-4"
          >
            Commissions & Services
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-4xl sm:text-5xl lg:text-6xl font-medium leading-tight mb-6"
            data-testid="commissions-hero-title"
          >
            Bring Aboriginal Art
            <br />
            Into Your World
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-base md:text-lg text-muted-foreground max-w-2xl mx-auto mb-10"
          >
            From bespoke reproductions to large-scale commercial installations,
            we work with collectors, designers, and businesses to create
            meaningful art experiences rooted in Gadigal and Bidjigal culture.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="flex flex-col sm:flex-row gap-4 justify-center"
          >
            <Button
              size="lg"
              className="rounded-full px-8 h-13 text-base shadow-lg shadow-primary/20"
              onClick={() => setQuoteOpen(true)}
              data-testid="commissions-request-quote-btn"
            >
              <MessageSquare className="mr-2 h-5 w-5" />
              Request a Quote
            </Button>
            <Link to="/products">
              <Button
                size="lg"
                variant="outline"
                className="rounded-full px-8 h-13 text-base"
              >
                Browse the Gallery
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      <Separator className="max-w-5xl mx-auto" />

      {/* Services Grid */}
      <section className="py-24 px-6 md:px-12">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-80px" }}
            variants={fadeUp}
            className="text-center mb-16"
          >
            <p className="text-sm uppercase tracking-[0.2em] text-primary mb-3">
              What We Offer
            </p>
            <h2 className="text-2xl sm:text-3xl font-medium">
              Services Tailored to You
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {SERVICES.map((svc, i) => (
              <motion.div
                key={svc.title}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-40px" }}
                custom={i}
                variants={fadeUp}
              >
                <Card
                  className="rounded-2xl h-full border-border/60 hover:border-primary/30 transition-colors"
                  data-testid={`service-card-${i}`}
                >
                  <CardContent className="pt-6 space-y-4">
                    <div className="w-11 h-11 rounded-xl bg-primary/10 flex items-center justify-center">
                      <svc.icon className="h-5 w-5 text-primary" />
                    </div>
                    <h3 className="font-medium text-lg">{svc.title}</h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {svc.description}
                    </p>
                    <ul className="space-y-2">
                      {svc.details.map((d) => (
                        <li
                          key={d}
                          className="flex items-start gap-2 text-sm text-muted-foreground"
                        >
                          <CheckCircle className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                          {d}
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-24 px-6 md:px-12 bg-muted/30">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-80px" }}
            variants={fadeUp}
            className="text-center mb-16"
          >
            <p className="text-sm uppercase tracking-[0.2em] text-primary mb-3">
              How It Works
            </p>
            <h2 className="text-2xl sm:text-3xl font-medium">
              From Brief to Beautiful
            </h2>
          </motion.div>

          <div className="grid sm:grid-cols-2 gap-8">
            {PROCESS_STEPS.map((step, i) => (
              <motion.div
                key={step.num}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-40px" }}
                custom={i}
                variants={fadeUp}
                className="flex gap-5"
                data-testid={`process-step-${step.num}`}
              >
                <span className="text-4xl font-bold text-primary/20 leading-none select-none">
                  {step.num}
                </span>
                <div>
                  <h3 className="font-medium mb-1">{step.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {step.text}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 px-6 md:px-12">
        <div className="max-w-3xl mx-auto text-center">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
          >
            <Image className="h-10 w-10 mx-auto mb-6 text-primary/60" />
            <h2 className="text-2xl sm:text-3xl font-medium mb-4">
              Ready to Start Your Project?
            </h2>
            <p className="text-muted-foreground mb-8 max-w-xl mx-auto">
              Whether it's a single piece for your home or a full commercial
              fit-out, we'd love to hear from you. Get in touch and let's create
              something meaningful together.
            </p>
            <Button
              size="lg"
              className="rounded-full px-10 h-13 text-base shadow-lg shadow-primary/20"
              onClick={() => setQuoteOpen(true)}
              data-testid="commissions-cta-quote-btn"
            >
              <MessageSquare className="mr-2 h-5 w-5" />
              Start a Conversation
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Quote Builder Modal */}
      <QuoteBuilder
        open={quoteOpen}
        onClose={() => setQuoteOpen(false)}
        artworkTitle="Custom Commission"
      />

      <Footer />
    </div>
  );
}
